/**
 * Full usage example (sync flow)
 *
 * 1) Ensure Liquid renders:
 *    <script type="application/json" data-custom-products-config data-custom-products-url="{{ 'custom-products.json' | asset_url }}"></script>
 *
 * 2) In JS, load config + calculate from a plain JSON object:
 *    const configEl = document.querySelector('[data-custom-products-config]');
 *    const allConfigs = loadCustomProducts(configEl);
 *
 *    const selection = {
 *      variantId: 52402560958753,
 *      inputs: {
 *        width: '72',
 *        height: '48',
 *        holes: '2',
 *        corners: '4',
 *        notches: '1',
 *        cuts: '0'
 *      },
 *    };
 *
 *    const productConfig = allConfigs[selection.variantId];
 *    if (!productConfig) throw new Error(`Missing custom config for variant ${variantId}`);
 *
 *    const result = calculateCustomProductPrice(productConfig, selection.inputs);
 *
 *    // Usable values
 *    const totalPriceCents = result.totalPriceCents;
 *    const totalPriceDisplay = formatMoney(totalPriceCents);
 */

/**
 * Load custom products configuration synchronously from a provided source.
 * Supported sources:
 * - Preloaded object map (returned as-is)
 * - HTMLElement containing JSON object text
 * - HTMLElement with `data-custom-products-url`
 * - Direct URL string
 * 
 * @param {HTMLElement | Record<string, unknown> | string} source - JSON script element, config object, or URL.
 * @returns {object} Object mapping variant IDs to product configurations
 */
function loadCustomProducts(source) {
  if (!source) {
    throw new Error('Missing custom products source.');
  }

  if (source && typeof source === 'object' && !Array.isArray(source) && !(source instanceof HTMLElement)) {
    return source;
  }

  /**
   * @param {string} url
   * @returns {object}
   */
  const loadFromUrlSync = (url) => {
    const request = new XMLHttpRequest();
    request.open('GET', url, false); // sync on purpose: caller requested non-async flow
    request.send(null);

    if (request.status < 200 || request.status >= 300) {
      throw new Error(`Failed to load custom products JSON (HTTP ${request.status}).`);
    }

    const parsed = JSON.parse(request.responseText || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Custom products JSON must be an object map.');
    }
    return parsed;
  };

  if (typeof source === 'string') {
    const url = source.trim();
    if (!url) throw new Error('Custom products URL is empty.');
    return loadFromUrlSync(url);
  }

  if (source instanceof HTMLElement) {
    const dataUrl = source.getAttribute('data-custom-products-url');
    if (dataUrl && dataUrl.trim()) {
      return loadFromUrlSync(dataUrl.trim());
    }

    const rawContent = (source.textContent || '').trim();
    if (!rawContent) {
      throw new Error('Custom products DOM node is empty and has no data-custom-products-url.');
    }

    try {
      const parsed = JSON.parse(rawContent);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      throw new Error('Custom products DOM JSON must be an object map.');
    } catch (err) {
      throw new Error(`Invalid custom products DOM JSON: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  throw new Error('Invalid custom products source. Expected HTMLElement or object map.');
}

/**
 * Extract input values from cart item properties
 * 
 * @param {Array<any> | Record<string, unknown> | null | undefined} properties - Cart item properties
 * @returns {object} Object with calculator input values
 */
function getInputValuesFromProperties(properties) {
  const values = {
    width: 0,
    height: 0,
    ramp_height: 0,
    panel_height: 0,
    diagonal: 0,
    holes: 0,
    corners: 0,
    notches: 0,
    cuts: 0,
    stair_steps: 0,
    sides: 0,
  };

  if (!properties) return values;

  /**
   * @param {string} name
   * @param {unknown} rawValue
   */
  const applyValue = (name, rawValue) => {
    const value = parseFloat(String(rawValue ?? '')) || 0;
    const normalizedName = String(name || '')
      .trim()
      .toLowerCase()
      .replace(/[_\-\s]/g, '');

    if (normalizedName === 'width') values.width = value;
    else if (normalizedName === 'height') values.height = value;
    else if (normalizedName === 'rampheight') values.ramp_height = value;
    else if (normalizedName === 'panelheight') values.panel_height = value;
    else if (normalizedName === 'diagonal') values.diagonal = value;
    else if (normalizedName === 'holes') values.holes = value;
    else if (normalizedName === 'corners') values.corners = value;
    else if (normalizedName === 'notches') values.notches = value;
    else if (normalizedName === 'cuts') values.cuts = value;
    else if (normalizedName === 'stairsteps') values.stair_steps = value;
    else if (normalizedName === 'sides') values.sides = value;
  };

  // /cart.js returns properties as an object, but some flows pass arrays
  if (Array.isArray(properties)) {
    properties.forEach(prop => {
      const name = prop?.name || '';
      applyValue(name, prop?.value);
    });
    return values;
  }

  if (typeof properties === 'object') {
    Object.entries(properties).forEach(([name, value]) => {
      applyValue(name, value);
    });
  }

  return values;
}

/**
 * Calculate square feet from width and height (in inches)
 * 
 * @param {number} widthIn - Width in inches
 * @param {number} heightIn - Height in inches
 * @returns {number} Square feet
 */
function calculateSquareFeet(widthIn, heightIn) {
  if (!widthIn || !heightIn) return 0;
  return (widthIn * heightIn) / 144; // Convert square inches to square feet
}

/**
 * Calculate perimeter from width and height (in inches).
 * NOTE: Kept legacy function name for compatibility, but returned value is in inches
 * because `perimeter_price` in the dataset is priced per inch.
 *
 * @param {number} widthIn - Width in inches
 * @param {number} heightIn - Height in inches
 * @returns {number} Perimeter in inches
 */
function calculatePerimeterInches(widthIn, heightIn) {
  if (!widthIn || !heightIn) return 0;
  return 2 * (widthIn + heightIn);
}

/**
 * Build calculator input object from raw inputs
 *
 * @param {{ width?: string | number, height?: string | number, ramp_height?: string | number, panel_height?: string | number, diagonal?: string | number, holes?: string | number, corners?: string | number, notches?: string | number, cuts?: string | number, stair_steps?: string | number, sides?: string | number }} inputs
 * @returns {{ width: number, height: number, ramp_height: number, panel_height: number, diagonal: number, holes: number, corners: number, notches: number, cuts: number, stair_steps: number, sides: number }}
 */
function normalizeCalculatorInputs(inputs) {
  /**
   * @param {unknown} value
   * @returns {number}
   */
  const toNumber = (value) => parseFloat(String(value ?? '')) || 0;

  return {
    // Width/height inputs are always interpreted as inches internally.
    // Unit toggle on the UI should convert the displayed value before calling this.
    width: toNumber(inputs.width),
    height: toNumber(inputs.height),
    ramp_height: toNumber(inputs.ramp_height),
    panel_height: toNumber(inputs.panel_height),
    diagonal: toNumber(inputs.diagonal),
    holes: toNumber(inputs.holes),
    corners: toNumber(inputs.corners),
    notches: toNumber(inputs.notches),
    cuts: toNumber(inputs.cuts),
    stair_steps: toNumber(inputs.stair_steps),
    sides: toNumber(inputs.sides),
  };
}

/**
 * Glass panel pricing calculator
 *
 * Formula:
 * (sqft * sqft_price) + (perimeter * perimeter_price) + additional_charges
 * +10% surcharge when sqft > 25
 *
 * @param {{ sqft_price?: string | number, perimeter_price?: string | number, additional_charges?: Record<string, string | number> }} productConfig
 * @param {{ width: number, height: number, holes: number, corners: number, notches: number, cuts: number }} inputs
 * @returns {{ squareFeet: number, perimeterFeet: number, totalPriceCents: number }}
 */
function calculateGlassPanelPrice(productConfig, inputs) {
  /**
   * @param {unknown} value
   * @returns {number}
   */
  const toNumber = (value) => parseFloat(String(value ?? '')) || 0;

  const squareFeet = calculateSquareFeet(inputs.width, inputs.height);
  const perimeterFeet = calculatePerimeterInches(inputs.width, inputs.height);

  const sqftPrice = toNumber(productConfig.sqft_price);
  const perimeterPrice = toNumber(productConfig.perimeter_price);
  const areaCharge = squareFeet * sqftPrice;
  const perimeterCharge = perimeterFeet * perimeterPrice;

  const additionalCharges = productConfig.additional_charges || {};
  /**
   * @param {string} key
   * @returns {number}
   */
  const getCharge = (key) => toNumber(additionalCharges[key]);
  const holeCharge = inputs.holes > 0 ? getCharge('holes') * inputs.holes : 0;
  const cornerCharge = inputs.corners > 0 ? getCharge('corners') * inputs.corners : 0;
  const notchCharge = inputs.notches > 0 ? getCharge('notches') * inputs.notches : 0;
  const cutCharge = inputs.cuts > 0 ? getCharge('cuts') * inputs.cuts : 0;
  const totalAdditionalCharges = holeCharge + cornerCharge + notchCharge + cutCharge;

  const subTotal = areaCharge + perimeterCharge + totalAdditionalCharges;
  const overcharge = squareFeet > 25 ? subTotal * 0.10 : 0;
  const totalPrice = subTotal + overcharge;

  return {
    squareFeet: squareFeet,
    perimeterFeet: perimeterFeet,
    totalPriceCents: Math.round(totalPrice * 100),
  };
}

/**
 * Stair ramp pricing calculator
 *
 * Formula:
 * (sqft * sqft_price) + (perimeter * perimeter_price) + additional_charges
 * +10% surcharge when sqft > 25
 *
 * @param {{ sqft_price?: string | number, perimeter_price?: string | number, additional_charges?: Record<string, string | number> }} productConfig
 * @param {{ width: number, height: number, ramp_height: number, panel_height: number, diagonal: number, holes: number, notches: number, sides: number }} inputs
 * @returns {{ squareFeet: number, perimeterFeet: number, totalPriceCents: number, numberOfPanels: number }}
 */
function calculateStairRampPrice(productConfig, inputs) {
  /**
   * @param {unknown} value
   * @returns {number}
   */
  const toNumber = (value) => parseFloat(String(value ?? '')) || 0;

  const numberOfPanels = Math.max(1, Math.ceil(inputs.diagonal / 80));

  const rampHeight = inputs.ramp_height || inputs.height;

  // Split only on width
  const panelWidth = inputs.width / numberOfPanels;
  const panelHeight = rampHeight;

  // Total area remains the same even when split into multiple panels
  const squareFeet = calculateSquareFeet(inputs.width, rampHeight);

  // Total perimeter = perimeter of one panel * number of panels
  const perimeterFeet =
    calculatePerimeterInches(panelWidth, panelHeight) * numberOfPanels;

  const sqftPrice = toNumber(productConfig.sqft_price);
  const perimeterPrice = toNumber(productConfig.perimeter_price);

  const areaCharge = squareFeet * sqftPrice;
  const perimeterCharge = perimeterFeet * perimeterPrice;

  const additionalCharges = productConfig.additional_charges || {};

  /**
   * @param {string} key
   * @returns {number}
   */
  const getCharge = (key) => toNumber(additionalCharges[key]);

  const holeCharge = inputs.holes > 0 ? getCharge('holes') * inputs.holes : 0;
  const cornerCharge = getCharge('corners') * 4 * numberOfPanels;
  const notchCharge = inputs.notches > 0 ? getCharge('notches') * inputs.notches : 0;
  const cutCharge = getCharge('cuts') * 2 * numberOfPanels;


  const totalAdditionalCharges =
    holeCharge + cornerCharge + notchCharge + cutCharge;

  const sides = inputs.sides || 1;

  const subTotal = areaCharge + perimeterCharge + totalAdditionalCharges;
  const overcharge = squareFeet > 25 ? subTotal * 0.10 : 0;
  const totalPrice = (subTotal + overcharge) * sides;

  return {
    squareFeet,
    perimeterFeet,
    numberOfPanels,
    totalPriceCents: Math.round(totalPrice * 100),
  };
}

/**
 * Registry of pricing calculators by custom product type.
 * Add new formulas by registering a calculator with `registerPriceCalculator`.
 * @type {Record<string, (productConfig: object, inputs: Record<string, number>) => { squareFeet: number, perimeterFeet: number, totalPriceCents: number }>}
 */
const PRICE_CALCULATORS_BY_TYPE = {
  'glass-panel': calculateGlassPanelPrice,
  'stair-ramp': calculateStairRampPrice,
};

/**
 * Register or override a pricing calculator for a specific product type.
 *
 * @param {string} type
 * @param {(productConfig: object, inputs: Record<string, number>) => { squareFeet: number, perimeterFeet: number, totalPriceCents: number }} calculator
 */
function registerPriceCalculator(type, calculator) {
  if (!type || typeof calculator !== 'function') return;
  PRICE_CALCULATORS_BY_TYPE[type] = calculator;
}

/**
 * Calculate custom product price by dispatching to the calculator associated with product type.
 * 
 * @param {{ type?: string, sqft_price?: string | number, perimeter_price?: string | number, additional_charges?: Record<string, string | number> }} productConfig - Product configuration from custom-products.json
 * @param {object} inputs - Input values object
 * @param {string | number} [inputs.width] - Width in inches
 * @param {string | number} [inputs.height] - Height in inches
 * @param {string | number} [inputs.holes] - Number of holes
 * @param {string | number} [inputs.corners] - Number of corners
 * @param {string | number} [inputs.notches] - Number of notches
 * @param {string | number} [inputs.cuts] - Number of angle cuts
 * @returns {{ squareFeet: number, perimeterFeet: number, totalPriceCents: number }} Object with calculated values
 */
function calculateCustomProductPrice(productConfig, inputs) {
  const productType = productConfig?.type || 'glass-panel';
  const calculator = PRICE_CALCULATORS_BY_TYPE[productType];
  if (!calculator) {
    return {
      squareFeet: 0,
      perimeterFeet: 0,
      totalPriceCents: 0,
    };
  }

  return calculator(productConfig, normalizeCalculatorInputs(inputs));
}

// Make functions available globally
if (typeof window !== 'undefined') {
  window.loadCustomProducts = loadCustomProducts;
  window.getInputValuesFromProperties = getInputValuesFromProperties;
  window.calculateSquareFeet = calculateSquareFeet;
  window.calculatePerimeterInches = calculatePerimeterInches;
  window.registerPriceCalculator = registerPriceCalculator;
  window.calculateCustomProductPrice = calculateCustomProductPrice;
}

if (typeof module !== "undefined" && module.exports) {
  module.exports = {
    loadCustomProducts: loadCustomProducts,
    calculateCustomProductPrice: calculateCustomProductPrice,
  };
}
